import { Grid } from '@material-ui/core';
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { Deficiency, DeficiencyListModified } from '../../models/models';
import type { DeficiencyComments as DeficiencyCommentsType } from '../../rxdb/deficiencyComments/deficiencyComments.types';
import useDeficiencyWithVersionComments from '../../rxdb/deficiencyComments/useDeficiencyWithVersionComments';
import type { InspectionListDocument } from '../../rxdb/inspectionList/inspectionList.types';
import type { TaskList } from '../../rxdb/taskList/taskList.types';
import { YYYYMMDD_DATE_FORMAT } from '../../utilities/constants';
import { InspectionStatus, InspectorRole, TaskStatus } from '../../utilities/enums';
import { isNull } from '../../utilities/utilityFunctions';
import useStyles from './DeficiencyComments.styles';

interface Props {
  deficiencyPayloadObj: DeficiencyListModified;
  originalDeficiencyPayload: Deficiency;
  validateInputs: () => void;
  updateValidComment: (flag: boolean) => void;
  inspection: InspectionListDocument;
  taskData: TaskList;
  isEditable: boolean;
  isActiveDeficiency: boolean;
  setDeficiencyPayload: (obj: DeficiencyListModified) => void;
}

const DeficiencyComments: React.FC<Props> = ({
  deficiencyPayloadObj,
  originalDeficiencyPayload,
  validateInputs,
  updateValidComment,
  inspection,
  taskData,
  isEditable,
  isActiveDeficiency,
  setDeficiencyPayload,
}) => {
  const classes = useStyles();
  const { CurrentUserSelected } = useMst();
  const deficiencyPayload = deficiencyPayloadObj;
  const currentInspectorId = CurrentUserSelected?.currentUser?.inspectorId;
  const currentClerkId = CurrentUserSelected?.currentUser?.clerkId;
  const [deficiencyComments, setDeficiencyComments] = useState<string>('');
  const [needsAttention, setNeedsAtention] = useState<boolean>(false);
  const [showComment, setShowComment] = useState<boolean>(false);
  const { previousDeficiencyComments } = useDeficiencyWithVersionComments(
    Number(deficiencyPayload?.componentFailureId),
    deficiencyPayload.componentVersionId,
    isActiveDeficiency,
    CurrentUserSelected.inspectionId,
  );

  useEffect(() => {
    if (deficiencyPayload) {
      setDeficiencyComments(deficiencyPayload?.commentInspectorField);
      setNeedsAtention(deficiencyPayload?.attentionFlag);
    }
  }, [deficiencyPayload]);

  const validateLeadForAddingComment = () => {
    const taskStatus = CurrentUserSelected.isAdhoc
      ? [TaskStatus.LI_PENDING_APPROVAL]
      : [TaskStatus.LI_PENDING_APPROVAL, TaskStatus.LI_SIGN_PENDING];
    const inspectionLevelStatus = CurrentUserSelected.isAdhoc
      ? [InspectionStatus.LI_PENDING_APPROVAL]
      : [InspectionStatus.LI_PENDING_APPROVAL, InspectionStatus.LI_SIGN_PENDING];
    return (
      taskData?.fkInspectorLead === currentInspectorId &&
      (!isActiveDeficiency ? deficiencyPayload?.leadInspectorId === currentInspectorId : true) &&
      taskStatus.includes(taskData?.fkInspectionTaskStatusCategory) &&
      inspectionLevelStatus.includes(inspection?.assetInspectionStatusId) &&
      taskData?.fkInspectorField !== taskData?.fkInspectorLead
    );
  };

  const validateFieldForAddingComment = () => {
    return (
      (taskData?.fkInspectorField === currentInspectorId || taskData?.clerkId === currentClerkId) &&
      [TaskStatus.IN_PROGRESS, TaskStatus.ASSIGNED].includes(
        taskData?.fkInspectionTaskStatusCategory,
      ) &&
      [InspectionStatus.IN_PROGRESS, InspectionStatus.ASSIGNED].includes(
        inspection?.assetInspectionStatusId,
      ) &&
      taskData?.isAssignedBack
    );
  };

  const validateLeadComment = () => {
    return (
      taskData?.fkInspectorLead === currentInspectorId &&
      (!isActiveDeficiency ? deficiencyPayload?.leadInspectorId === currentInspectorId : true)
    );
  };

  const handleCancelComment = async () => {
    setDeficiencyComments(isNull(originalDeficiencyPayload, 'commentInspectorField', ''));
    setNeedsAtention(originalDeficiencyPayload?.attentionFlag);
    deficiencyPayload.commentInspectorField = isNull(
      originalDeficiencyPayload,
      'commentInspectorField',
      '',
    );
    deficiencyPayload.attentionFlag = originalDeficiencyPayload?.attentionFlag;
    validateInputs();
    setShowComment(false);
  };

  const handleSaveComment = async () => {
    deficiencyPayload.commentInspectorField = deficiencyComments;
    deficiencyPayload.attentionFlag = needsAttention;
    if (isActiveDeficiency) {
      setDeficiencyPayload({ ...deficiencyPayloadObj });
    }
    validateInputs();
    setShowComment(false);
  };

  const onLeadInspectorCommentChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setDeficiencyComments(value);
    setShowComment(true);
  };

  const handleNeedAttentionChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await setNeedsAtention(event.target.checked);
    setShowComment(true);
  };

  const onFieldInspectorCommentChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setDeficiencyComments(value);
    setShowComment(true);
  };

  const getDateTimeFormat = (dateTime: string) => {
    const dateUTC = new Date(dateTime);
    const dateUTCNum = dateUTC.getTime();
    const dateIST = new Date(dateUTCNum);
    dateIST.setHours(dateIST.getHours() + 6);
    return dayjs(dateIST).format(
      `\u00A0\u00A0\u00A0\u00A0${YYYYMMDD_DATE_FORMAT}\u00A0\u00A0\u00A0\u00A0HH:mm:ss`,
    );
  };

  const validateComments = () => {
    // condition to show comment section. Checking FI and LI eligiblitlity to add comments.
    return (
      previousDeficiencyComments?.length > 0 ||
      validateLeadForAddingComment() ||
      validateFieldForAddingComment()
    );
  };

  const checkValid = () => {
    return validateLeadComment()
      ? (deficiencyComments === isNull(originalDeficiencyPayload, 'commentInspectorField', '') &&
          needsAttention === originalDeficiencyPayload?.attentionFlag) ||
          (needsAttention && !deficiencyComments?.length)
      : deficiencyComments === isNull(deficiencyPayload, 'commentInspectorField', '');
  };

  const checkValidNeedForAttentionComment = () => {
    return checkValid() || (needsAttention && deficiencyComments?.trim().length < 1);
  };

  useEffect(() => {
    updateValidComment(
      needsAttention
        ? (showComment && !checkValid()) || deficiencyComments?.trim()?.length < 1
        : showComment && !checkValid(),
    );
  }, [checkValid, showComment]);

  return (
    <div className={classes.deficiencyCommentsOuterGrid}>
      {validateComments() && (
        <Card elevation={1} className="deficiencyCommentsInnerGrid">
          <Grid container xs={12} className={classes.defCommentsActionGrid}>
            <Grid item xs={8}>
              <Typography variant="h3" fontWeight={300}>
                Comments
              </Typography>
            </Grid>
            <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
              {(!checkValid() || needsAttention) && (
                <>
                  <Grid
                    item
                    className={showComment ? classes.cancelButtonGrid : classes.hideCancelButton}
                  >
                    <Button
                      noMinHeight
                      hidden={!showComment}
                      color="primary"
                      variant="outlined"
                      className="commentsCancelButton"
                      onClick={handleCancelComment}
                    >
                      <Typography>Cancel</Typography>
                    </Button>
                  </Grid>
                  <Grid item className={!showComment ? classes.hideSaveButton : ' '}>
                    <Button
                      disabled={checkValidNeedForAttentionComment()}
                      noMinHeight
                      hidden={!showComment}
                      color="primary"
                      variant="contained"
                      onClick={handleSaveComment}
                      className={
                        checkValidNeedForAttentionComment() ? 'showSaveButton' : 'disableSaveButton'
                      }
                    >
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        className={classes.submitInspectionText}
                      >
                        Save
                      </Typography>
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Card elevation={1} className="deficiencyCardContainer">
            {validateLeadForAddingComment() && (
              <Grid container xs={12}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  className={classes.deficiencyCommentsLabel}
                >
                  Lead Inspector Comments
                </Typography>

                <TextField
                  disabled={!validateLeadForAddingComment}
                  noMinHeight
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  className={classes.deficiencyLeadCommentsTextField}
                  defaultValue={isNull(deficiencyPayload, 'commentInspectorField', '')}
                  error={needsAttention && !deficiencyComments?.trim()?.length}
                  errorText="Comment is mandatory if need attention checked"
                  value={deficiencyComments}
                  required={deficiencyPayload?.attentionFlag}
                  debounceTime={7000}
                  onChange={onLeadInspectorCommentChange}
                />

                <Grid container xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={needsAttention || false}
                        onChange={handleNeedAttentionChange}
                        disabled={!validateLeadForAddingComment}
                      />
                    }
                    label="Need Attention for this Inspection"
                  />
                  <Grid className={classes.checkBoxText}>
                    <Typography className={classes.subtitle}>
                      (check if revision required for the Inspector)
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {validateFieldForAddingComment() && (
              <Grid container xs={12}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  className={classes.deficiencyCommentsLabel}
                >
                  {taskData?.clerkId === currentClerkId ? 'Data Entry Clerk' : 'Field Inspector'}{' '}
                  Comments
                </Typography>

                <TextField
                  inputProps={{
                    'data-testid': 'fi-def-comments',
                  }}
                  disabled={!isEditable}
                  noMinHeight
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  className={classes.deficiencyLeadCommentsTextField}
                  defaultValue={isNull(deficiencyPayload, 'commentInspectorField', '')}
                  value={deficiencyComments}
                  required={deficiencyPayload?.attentionFlag}
                  debounceTime={7000}
                  onChange={onFieldInspectorCommentChange}
                />
              </Grid>
            )}
            {previousDeficiencyComments?.length > 0 && (
              <>
                <Typography variant="subtitle2" className="previousCmts">
                  Previous Comments
                </Typography>
                {previousDeficiencyComments &&
                  previousDeficiencyComments.map((e: DeficiencyCommentsType) => (
                    <>
                      <Typography variant="subtitle2" className="previousCmts commentDate">
                        {e.inspectorRoleId === InspectorRole.LEAD &&
                          `Lead Inspector Review Findings (${e.inspectorName})`}
                        {e.inspectorRoleId === InspectorRole.SC &&
                          `Site Coordinator Review Findings (${e.inspectorName})`}
                        {e.inspectorRoleId === InspectorRole.FIELD &&
                          `Field Inspector Comments (${e.inspectorName})`}
                        {e.inspectorRoleId === InspectorRole.DEC &&
                          `Data Entry Clerk Comments (${e.clerkName})`}
                        {`${getDateTimeFormat(e.dateCreated as string)}`}
                      </Typography>
                      <TextField
                        disabled
                        noMinHeight
                        fullWidth
                        variant="outlined"
                        size="small"
                        minRows="2"
                        multiline
                        className="deficiencyInspectorComment"
                        defaultValue={e?.comment ? e?.comment : ''}
                        debounceTime={7000}
                      />
                      {(e.inspectorRoleId === InspectorRole.LEAD ||
                        e.inspectorRoleId === InspectorRole.SC) &&
                        e.attentionFlag && (
                          <Grid container xs={12}>
                            <FormControlLabel
                              disabled
                              control={
                                <Checkbox disabled color="primary" checked={e.attentionFlag} />
                              }
                              label="Need Attention for this Inspection"
                            />
                          </Grid>
                        )}
                    </>
                  ))}
              </>
            )}
          </Card>
        </Card>
      )}
    </div>
  );
};

export default DeficiencyComments;
